import { AxiosInstance } from 'axios'
import { IJsonApiSerializer } from '../JsonApiSerializer'

class AdminService {
  public httpClient: AxiosInstance
  public serializer: IJsonApiSerializer

  constructor(httpClient: AxiosInstance, serializer: IJsonApiSerializer) {
    this.httpClient = httpClient
    this.serializer = serializer
  }

  public getDataFromJson(JsonData) {
    const data = this.serializer.parseResourceData(JsonData, JsonData.data)
    const pagination = { ...JsonData.meta }

    return { data, pagination }
  }

  public getIdentificationsFromJson(data) {
    const identifications = this.serializer.parseResourceData(data, data.data)
    const pagination = { ...data.meta }

    return { identifications, pagination }
  }

  public async approveIdentification(id) {
    const { data } = await this.httpClient.put(`/admin/api/identifications/${id}/approve`)
    const identification = this.serializer.parseResourceData(
      data.identification,
      data.identification.data
    )

    return { identification, flush: data.flush }
  }

  public async declineIdentification(id) {
    const { data } = await this.httpClient.put(`/admin/api/identifications/${id}/decline`)
    const identification = this.serializer.parseResourceData(
      data.identification,
      data.identification.data
    )

    return { identification, flush: data.flush }
  }

  public async createCategory(category) {
    const { data } = await this.httpClient.post(`/admin/api/categories`, { category })
    const createdCategory = this.serializer.parseResourceData(data.category, data.category.data)

    return { createdCategory, flush: data.flush }
  }

  public async updateCategory(category) {
    const { data } = await this.httpClient.patch(`/admin/api/categories/${category.id}`, {
      category,
    })
    const updatedCategory = this.serializer.parseResourceData(data.category, data.category.data)

    return { updatedCategory, flush: data.flush }
  }

  public async updateCategories(categories_ids) {
    const { data } = await this.httpClient.post(`/admin/api/update_all_categories`, {
      categories_ids,
    })
    const updatedCategories = this.serializer.parseResourceData(
      data.categories,
      data.categories.data
    )

    return { updatedCategories, flush: data.flush }
  }

  public async deleteCategory(id) {
    const { data } = await this.httpClient.delete(`/admin/api/categories/${id}`)

    return { flush: data.flush }
  }

  public async createPage(page) {
    const { data } = await this.httpClient.post('/admin/api/pages', { page })
    const createdPage = this.serializer.parseResourceData(data.page, data.page.data)

    return { createdPage, flush: data.flush }
  }

  public async updatePage(id, page) {
    const { data } = await this.httpClient.patch(`/admin/api/pages/${id}`, { page })
    const updatedPage = this.serializer.parseResourceData(data.page, data.page.data)

    return { updatedPage, flush: data.flush }
  }

  public async deletePage(id) {
    const { data } = await this.httpClient.delete(`/admin/api/pages/${id}`)

    return { flush: data.flush }
  }

  public async previewPage(content: string) {
    const { data } = await this.httpClient.post('/admin/api/pages/preview', { content })

    return { html: data.html }
  }

  public async createArea(formData) {
    const { data } = await this.httpClient.post(`/admin/api/areas`, formData)
    const createdArea = this.serializer.parseResourceData(data.area, data.area.data)

    return { createdArea, flush: data.flush }
  }

  public async updateArea(id, formData) {
    const { data } = await this.httpClient.patch(`/admin/api/areas/${id}`, formData)
    const updatedArea = this.serializer.parseResourceData(data.area, data.area.data)

    return { updatedArea, flush: data.flush }
  }

  public async updateAreas(areas_ids) {
    const { data } = await this.httpClient.post(`/admin/api/update_all_areas`, {
      areas_ids,
    })
    const updatedAreas = this.serializer.parseResourceData(data.areas, data.areas.data)

    return { updatedAreas, flush: data.flush }
  }

  public async deleteArea(id) {
    const { data } = await this.httpClient.delete(`/admin/api/areas/${id}`)

    return { flush: data.flush }
  }

  public async createFeature(formData) {
    const { data } = await this.httpClient.post(`/admin/api/features`, formData)
    const createdFeature = this.serializer.parseResourceData(data.feature, data.feature.data)

    return { createdFeature, flush: data.flush }
  }

  public async updateFeature(id, formData) {
    const { data } = await this.httpClient.patch(`/admin/api/features/${id}`, formData)
    const updatedFeature = this.serializer.parseResourceData(data.feature, data.feature.data)

    return { updatedFeature, flush: data.flush }
  }

  public async updateFeatures(features_ids) {
    const { data } = await this.httpClient.post(`/admin/api/update_all_features`, {
      features_ids,
    })
    const updatedFeatures = this.serializer.parseResourceData(data.features, data.features.data)

    return { updatedFeatures, flush: data.flush }
  }

  public async deleteFeature(id) {
    const { data } = await this.httpClient.delete(`/admin/api/features/${id}`)

    return { flush: data.flush }
  }

  public async createPickup(pickup) {
    const { data } = await this.httpClient.post(`/admin/api/pickups`, { pickup })
    const createdPickup = this.serializer.parseResourceData(data.pickup, data.pickup.data)

    return { createdPickup, flush: data.flush }
  }

  public async updatePickups(pickup_ids) {
    const { data } = await this.httpClient.post(`/admin/api/update_all_pickups`, {
      pickup_ids,
    })
    const updatedPickups = this.serializer.parseResourceData(data.pickups, data.pickups.data)

    return { updatedPickups, flush: data.flush }
  }

  public async deletePickup(id) {
    const { data } = await this.httpClient.delete(`/admin/api/pickups/${id}`)

    return { flush: data.flush }
  }

  public async createTag(tag) {
    const { data } = await this.httpClient.post('/admin/api/tags/', { tag })
    const createdTag = this.serializer.parseResourceData(data.tag, data.tag.data)

    return { createdTag, flush: data.flush }
  }

  public async updateTag(tag) {
    const { data } = await this.httpClient.patch(`/admin/api/tags/${tag.id}`, { tag })
    const updatedTag = this.serializer.parseResourceData(data.tag, data.tag.data)

    return { updatedTag, flush: data.flush }
  }

  public async updateTags(tags_ids) {
    const { data } = await this.httpClient.post(`/admin/api/update_all_tags`, {
      tags_ids,
    })
    const updatedTags = this.serializer.parseResourceData(data.tags, data.tags.data)

    return { updatedTags, flush: data.flush }
  }

  public async deleteTag(id) {
    return this.httpClient.delete(`/admin/api/tags/${id}`)
  }

  public async deletePost(id) {
    return await this.httpClient.delete(`/admin/api/posts/${id}`)
  }

  public async searchPost(params) {
    const { data } = await this.httpClient.get('/admin/api/posts/search', { params })
    const posts = this.serializer.parseResourceData(data.posts.posts, data.posts.posts.data)
    const pagination = data.posts.posts.meta

    return { posts, pagination }
  }

  public async getPostById(id) {
    const { data } = await this.httpClient.get(`/admin/api/posts/${id}`)
    const post = this.serializer.parseResourceData(data.post, data.post.data)
    return { post }
  }

  public async createUser(user) {
    const { data } = await this.httpClient.post('/admin/api/users', user)
    const createdUser = this.serializer.parseResourceData(data.user, data.user.data)

    return { createdUser, flush: data.flush }
  }

  public async updateUser(param) {
    const { data } = await this.httpClient.patch(`/admin/api/users/${param.id}`, { user: param })
    const updatedUser = this.serializer.parseResourceData(data.user, data.user.data)

    return { updatedUser, flush: data.flush }
  }

  public async deleteUser(id) {
    const {
      data: { flush },
    } = await this.httpClient.delete(`/admin/api/users/${id}`)

    return { flush }
  }

  public async inactivateUser(id) {
    const {
      data: { user, flush },
    } = await this.httpClient.put(`/admin/api/users/${id}/inactivate`)

    return { user, flush }
  }

  public async activateUser(id) {
    const {
      data: { user, flush },
    } = await this.httpClient.put(`/admin/api/users/${id}/activate`)

    return { user, flush }
  }

  public async searchUsers(params) {
    const { data } = await this.httpClient.get('/admin/api/users/search', { params })
    const users = this.serializer.parseResourceData(data.users.users, data.users.users.data)
    const pagination = data.users.users.meta

    return { users, pagination }
  }

  public async getUserRelations(id) {
    const { data } = await this.httpClient.get(`/admin/api/users/${id}/relations`)
    const users = this.serializer.parseResourceData(data.users, data.users.data)

    return { users }
  }

  public async deleteReview(id) {
    const {
      data: { flush },
    } = await this.httpClient.delete(`/admin/api/reviews/${id}`)

    return { flush }
  }

  public async createPromotion(promotion) {
    const { data } = await this.httpClient.post(`/admin/api/promotions`, { promotion })
    const createdPromotion = this.serializer.parseResourceData(data.promotion, data.promotion.data)

    return { createdPromotion, flush: data.flush }
  }

  public async updatePromotion(promotion) {
    const { data } = await this.httpClient.patch(`/admin/api/promotions/${promotion.id}`, {
      promotion,
    })
    const updatedPromotion = this.serializer.parseResourceData(data.promotion, data.promotion.data)

    return { updatedPromotion, flush: data.flush }
  }

  public async deletePromotion(id) {
    const { data } = await this.httpClient.delete(`/admin/api/promotions/${id}`)

    return { flush: data.flush }
  }

  public async createCoupon(coupon) {
    const { data } = await this.httpClient.post(`/admin/api/coupons`, { coupon })
    const createdCoupon = this.serializer.parseResourceData(data.coupon, data.coupon.data)

    return { createdCoupon, flush: data.flush }
  }

  public async updateCoupon(id, coupon) {
    const { data } = await this.httpClient.put(`/admin/api/coupons/${id}`, {
      coupon,
    })
    const updatedCoupon = this.serializer.parseResourceData(data.coupon, data.coupon.data)

    return { updatedCoupon, flush: data.flush }
  }

  public async deleteCoupon(id) {
    const { data } = await this.httpClient.delete(`/admin/api/coupons/${id}`)

    return { flush: data.flush }
  }
}

export default AdminService
